import type { HTMLAttributes, ReactNode } from "react"
import { createContext, forwardRef, useContext } from "react"

import { cn } from "@productlane/lib/src/cn"
import { Tooltip } from "@productlane/ui"

interface PropertyTriggerProps extends HTMLAttributes<HTMLDivElement> {
	icon: ReactNode
	tooltip?: ReactNode
}

export const propertyIconClassName = "h-4 w-4 flex-none text-primary"

export const propertyChipClassName =
	"flex h-7 items-center gap-1.5 rounded-md px-1.5 group-hover:bg-gray-150 dark:group-hover:bg-gray-800"

export const propertyPillClassName =
	"flex h-7 items-center gap-1.5 rounded-full border border-gray-200 px-2.5 group-hover:bg-gray-100 dark:border-gray-700/80 dark:group-hover:bg-gray-800"

const PropertyPillContext = createContext(false)

/** Renders every property chip inside it as a bordered pill, the way Linear's project page does. */
export const PropertyPills = PropertyPillContext.Provider

// Popover hands its trigger props and a ref through `asChild`, so both reach the root.
export const PropertyTrigger = forwardRef<HTMLDivElement, PropertyTriggerProps>(
	({ tooltip, icon, children, className, ...props }, ref) => {
		const asPill = useContext(PropertyPillContext)
		const chip = (
			<div className={asPill ? propertyPillClassName : propertyChipClassName}>
				<span className="flex h-4 min-w-4 flex-none items-center justify-center">
					{icon}
				</span>
				<span className="max-w-48 select-none truncate text-sm text-primary">
					{children}
				</span>
			</div>
		)
		return (
			<div ref={ref} className={cn("group", className)} {...props}>
				{tooltip ? (
					<Tooltip label={tooltip} shouldWrapChildren>
						{chip}
					</Tooltip>
				) : (
					chip
				)}
			</div>
		)
	},
)
